/*
 * Selo PDP behaviour
 * ------------------
 * Dependency-free custom elements, following Dawn's custom-element +
 * pub/sub conventions. Registered: <selo-side-sheet>, <selo-gallery>,
 * <selo-quick-order>. (Accordion is native <details>. The pack selector
 * lives in $lib/components/pack-selector.ts as an Alpine component.)
 */
(function () {
  "use strict";

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  /* ---------------- Reusable side sheet (right drawer) ----------------
   * Fully document-delegated so opening works regardless of whether the
   * <selo-side-sheet> custom element has finished upgrading. Focus trap,
   * Esc, overlay/close, scroll lock, focus restore — all handled here.
   */
  var openSheet = null; // enforce one-at-a-time

  // Single shared overlay — rendered once by snippets/selo-sheet-overlay.liquid.
  // selo-pdp.js only toggles its `open` attribute, same as the sheets.
  function overlayEl() {
    return document.querySelector(".selo-sheet-overlay");
  }

  function openSheetEl(el, trigger) {
    if (!el) return;
    if (openSheet && openSheet !== el) closeSheetEl(openSheet);
    el._seloTrigger = trigger || document.activeElement;
    // Portal the sheet to <body> so it escapes the sticky .selo-pdp__buy
    // stacking context (position:sticky creates one) — otherwise it mis-layers
    // against the overlay. Tokens live on :root, so styling survives the move.
    if (el.parentNode !== document.body) document.body.appendChild(el);
    el.setAttribute("open", "");
    var overlay = overlayEl();
    if (overlay) overlay.setAttribute("open", "");
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onSheetKeydown);
    openSheet = el;
    var panel = el.querySelector(".selo-sheet__panel");
    if (panel) {
      var first = panel.querySelector(FOCUSABLE);
      if (first) first.focus();
    }
  }

  // Public helper for inline handlers (e.g. "Jump to reviews" link).
  window.SeloSheet = {
    close: function (el) {
      closeSheetEl(el);
    },
  };

  function closeSheetEl(el) {
    if (!el) return;
    el.removeAttribute("open");
    var overlay = overlayEl();
    if (overlay) overlay.removeAttribute("open");
    document.body.style.overflow = "";
    document.removeEventListener("keydown", onSheetKeydown);
    if (openSheet === el) openSheet = null;
    var trigger = el._seloTrigger;
    if (trigger && typeof trigger.focus === "function") trigger.focus();
  }

  function onSheetKeydown(e) {
    if (!openSheet) return;
    if (e.key === "Escape") {
      e.preventDefault();
      closeSheetEl(openSheet);
      return;
    }
    if (e.key !== "Tab") return;
    var panel = openSheet.querySelector(".selo-sheet__panel");
    if (!panel) return;
    var nodes = Array.prototype.slice
      .call(panel.querySelectorAll(FOCUSABLE))
      .filter(function (n) {
        return n.offsetParent !== null;
      });
    if (!nodes.length) return;
    var firstEl = nodes[0];
    var lastEl = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === firstEl) {
      e.preventDefault();
      lastEl.focus();
    } else if (!e.shiftKey && document.activeElement === lastEl) {
      e.preventDefault();
      firstEl.focus();
    }
  }

  // <selo-side-sheet> exists so the markup is semantic and we get the
  // lifecycle hook if we ever need it; behaviour lives in delegated handlers.
  if (!customElements.get("selo-side-sheet")) {
    customElements.define("selo-side-sheet", class extends HTMLElement {});
  }

  document.addEventListener("click", function (e) {
    // Open: anything with [data-selo-sheet="<sheet-id>"]
    var trigger = e.target.closest("[data-selo-sheet]");
    if (trigger) {
      var id = trigger.getAttribute("data-selo-sheet");
      var sheet = document.getElementById(id);
      if (sheet) {
        e.preventDefault();
        openSheetEl(sheet, trigger);
        return;
      }
      // Visible warning so a wiring mistake doesn't fail silently.
      if (window.console)
        console.warn("[selo] side sheet not found for id:", id);
      return;
    }
    // Close: click on the overlay closes the active sheet.
    if (e.target.closest(".selo-sheet-overlay")) {
      if (openSheet) closeSheetEl(openSheet);
      return;
    }
    // Close: close button inside a sheet.
    var closer = e.target.closest(".selo-sheet__close");
    if (closer) {
      var sheetEl = closer.closest(".selo-sheet");
      if (sheetEl) {
        e.preventDefault();
        closeSheetEl(sheetEl);
      }
    }
  });

  /* ---------------- Compact gallery (thumb → primary) ---------------- */
  class SeloGallery extends HTMLElement {
    connectedCallback() {
      this.primary = this.querySelector("[data-selo-primary]");
      var self = this;
      this.querySelectorAll(".selo-gallery__thumb").forEach(function (thumb) {
        thumb.addEventListener("click", function () {
          if (!self.primary) return;
          self.primary.src = thumb.dataset.full;
          self.primary.alt = thumb.dataset.alt || "";
          self.querySelectorAll(".selo-gallery__thumb").forEach(function (t) {
            t.setAttribute("aria-current", String(t === thumb));
          });
        });
      });
    }
  }
  if (!customElements.get("selo-gallery"))
    customElements.define("selo-gallery", SeloGallery);

  /* ---------------- Quick-order related card (inline add) ---------------- */
  class SeloQuickOrder extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.querySelectorAll("[data-selo-qo-add]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          var card = btn.closest("[data-selo-qo-card]");
          var variantId = card.getAttribute("data-variant-id");
          var qtyEl = card.querySelector("[data-selo-qo-qty]");
          var qty = parseInt(qtyEl && qtyEl.value, 10) || 1;
          if (!variantId) return;
          btn.disabled = true;
          var original = btn.textContent;
          btn.textContent = "Adding…";
          fetch(
            (window.routes && window.routes.cart_add_url
              ? window.routes.cart_add_url
              : "/cart/add") + ".js",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({
                items: [{ id: variantId, quantity: qty }],
              }),
            },
          )
            .then(function (r) {
              return r.json();
            })
            .then(function () {
              btn.textContent = "Added ✓";
              if (
                typeof window.publish === "function" &&
                window.PUB_SUB_EVENTS
              ) {
                window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
                  source: "selo-quick-order",
                });
              }
              setTimeout(function () {
                btn.textContent = original;
                btn.disabled = false;
              }, 1600);
            })
            .catch(function () {
              btn.textContent = original;
              btn.disabled = false;
            });
        });
      });
    }
  }
  if (!customElements.get("selo-quick-order"))
    customElements.define("selo-quick-order", SeloQuickOrder);
})();
